import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useTranslation } from 'react-i18next';
import { FaShoppingCart, FaUser, FaSignOutAlt, FaBox, FaUsers, FaTachometerAlt, FaGlobe } from 'react-icons/fa';
import '../styles/Header.css';

const Header = () => {
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const { cart, getCartTotal } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 70, right: 20 });
  const langMenuRef = useRef(null);
  const langBtnRef = useRef(null);
  const cartItemsCount = cart.length;
  const cartTotal = getCartTotal();

  const languages = {
    et: 'Eesti',
    en: 'English',
    ru: 'Русский',
    az: 'Azərbaycan',
    hy: 'Հայերեն',
    ka: 'ქართული',
    it: 'Italiano',
    hi: 'हिन्दी'
  };

  // Обработчик переключения меню языков
  const toggleLangMenu = () => {
    // Вычисляем позицию кнопки
    if (langBtnRef.current && !showLangMenu) {
      const rect = langBtnRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 12, // 8px ниже кнопки
        right: window.innerWidth - rect.right - 30 // выравнивание по правому краю кнопки
      });
    }

    setShowLangMenu(prev => !prev);
  };

  // Закрытие меню языков при клике вне его области
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langMenuRef.current && !langMenuRef.current.contains(event.target)) {
        setShowLangMenu(false);
      }
    };

    if (showLangMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLangMenu]);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('i18nextLng', lng);
    setShowLangMenu(false);
  };

  // Получаем текущий язык (первые 2 символа)
  const currentLang = i18n.language ? i18n.language.split('-')[0] : 'et';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <header className="header">
      <div className="header-container">
        <Link to="/" className="logo">
          <img src="/images/logo.png" alt="HanseFood" className="logo-image" />
          {/* <span className="logo-text">HanseFood</span> */}
        </Link>

        <nav className="nav">
          {isAuthenticated ? (
            <>
              <div className="nav-links">
                <Link to="/catalog" className={`nav-link ${isActive('/catalog') || isActive('/product') ? 'active' : ''}`}>
                  <FaBox /> {t('catalog')}
                </Link>

                {!isAdmin && (
                  <>
                    <Link to="/orders" className={`nav-link ${isActive('/orders') ? 'active' : ''}`}>
                      {t('orders')}
                    </Link>
                  </>
                )}


                {isAdmin && (
                  <>
                    <Link to="/admin/dashboard" className={`nav-link ${isActive('/admin/dashboard') ? 'active' : ''}`}>
                      <FaTachometerAlt /> {t('dashboard')}
                    </Link>
                    <Link to="/admin/products" className={`nav-link ${isActive('/admin/products') ? 'active' : ''}`}>
                      {t('products')}
                    </Link>
                    <Link to="/admin/orders" className={`nav-link ${isActive('/admin/orders') ? 'active' : ''}`}>
                      {t('orders')}
                    </Link>
                    <Link to="/admin/users" className={`nav-link ${isActive('/admin/users') ? 'active' : ''}`}>
                      <FaUsers /> {t('clients')}
                    </Link>
                    <Link to="/admin/branches" className={`nav-link ${isActive('/admin/branches') ? 'active' : ''}`}>
                      Telema
                    </Link>
                    <Link to="/admin/nested-subgroups" className={`nav-link ${isActive('/admin/nested-subgroups') ? 'active' : ''}`}>
                      {t('nestedSubgroups', 'Подгруппы')}
                    </Link>
                  </>
                )}
              </div>

              <div className="nav-right">
                {!isAdmin && (
                  <Link to="/cart" className={`nav-link cart-link ${isActive('/cart') || isActive('/checkout') ? 'active' : ''}`}>
                    <FaShoppingCart />
                    <span className="cart-text">
                      {t('cart')}
                      {cartItemsCount > 0 && (
                        <span className="cart-total"> ({cartTotal.toFixed(2)} €)</span>
                      )}
                    </span>
                    {cartItemsCount > 0 && (
                      <span className="cart-badge">{cartItemsCount}</span>
                    )}
                  </Link>
                )}

                <div className="language-switcher" ref={langMenuRef}>
                  <button
                    ref={langBtnRef}
                    className="lang-btn"
                    onClick={toggleLangMenu}
                    type="button"
                    aria-label="Выбрать язык"
                  >
                    <FaGlobe /> <span className="lang-text">{languages[currentLang] || languages['et']}</span>
                  </button>
                  {showLangMenu && (
                    <div
                      className="language-menu"
                      style={{
                        top: `${menuPosition.top}px`,
                        right: `${menuPosition.right}px`
                      }}
                    >
                      {Object.keys(languages).map((lng) => (
                        <button
                          key={lng}
                          onClick={() => changeLanguage(lng)}
                          className={`lang-option ${currentLang === lng ? 'active' : ''}`}
                          type="button"
                        >
                          {languages[lng]}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="user-menu">
                  <Link to="/profile" className={`nav-link ${isActive('/profile') ? 'active' : ''}`}>
                    <FaUser />
                    {user?.restaurantName || user?.email}
                  </Link>
                  <button onClick={handleLogout} className="nav-link logout-btn">
                    <FaSignOutAlt /> {t('logout')}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="language-switcher" ref={langMenuRef}>
                <button
                  ref={langBtnRef}
                  className="lang-btn"
                  onClick={toggleLangMenu}
                  type="button"
                  aria-label="Выбрать язык"
                >
                  <FaGlobe /> <span className="lang-text">{languages[currentLang] || languages['et']}</span>
                </button>
                {showLangMenu && (
                  <div
                    className="language-menu"
                    style={{
                      top: `${menuPosition.top}px`,
                      right: `${menuPosition.right}px`
                    }}
                  >
                    {Object.keys(languages).map((lng) => (
                      <button
                        key={lng}
                        onClick={() => changeLanguage(lng)}
                        className={`lang-option ${currentLang === lng ? 'active' : ''}`}
                        type="button"
                      >
                        {languages[lng]}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <Link to="/login" className="nav-link">
                {t('login')}
              </Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
};

export default Header;

